/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import { GoodsVenueSelector } from "components/event/goods/GoodsVenueSelector";
import { AddToCartButton } from "components/event/goods/detail/AddToCartButton";
import { EventGoodsStatusBadges } from "features/eventGoods/EventGoodsStatusBadges";
import { WatchlistIconButton } from "features/watchlist/WatchlistIconButton";
import { useI18n } from "hooks/i18n/i18n";
import { withNoTranslate } from "i18n/noTranslate";
import { eventActions } from "modules/event";
import { modalActions } from "modules/modal";
import React, { Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FeatureEnabled } from "tests/FeatureEnabled";
import { AnalyticsProvider } from "utility/ga/AnalyticsContext";
import { getDisplayPrice, isShippingItem } from "utility/ticket";

import { cartActions } from "../../../modules/cart";
import { isDisplayPeriod } from "../../../utility";
import { isFeatureEnabled } from "../../../utility/isFeatureEnabled";
import { sanitizer } from "../../../utility/sanitize";
import { ActionButtons } from "./EventGoodsItemInfo/ActionButtons/ActionButtons";
import { AddToCartButtonV2 } from "./EventGoodsItemInfo/AddToCartButtonV2/AddToCartButtonV2";
import { Amount } from "./EventGoodsItemInfo/Amount/Amount";
import { FulfillmentSelect } from "./EventGoodsItemInfo/FulfillmentSelect/FulfillmentSelect";
import { GoodsName } from "./EventGoodsItemInfo/GoodsName/GoodsName";
import { Price } from "./EventGoodsItemInfo/Price/Price";
import { TagList } from "./EventGoodsItemInfo/TagList/TagList";
import { Variant } from "./EventGoodsItemInfo/Variant/Variant";
import { VenueLabel } from "./EventGoodsItemInfo/VenueLabel/VenueLabel";
import { WatchListButton } from "./EventGoodsItemInfo/WatchListButton/WatchListButton";
import { useEventGoodsItemInfo } from "./EventGoodsItemInfo/useEventGoodsItemInfo";

import type { ticketState } from "modules/ticket";
import type { ComponentProps } from "react";
import type { Store } from "store";

/**
 * カートへ追加するためのリクエスト値となる商品
 */
type CartProduct = {
  id: string;
  productType: "goods";
  count: number;
  price: number;
  name: string;
  variantName: string;
};

type EventGoodsItemInfoProps = {
  eventId: string;
  classGoodsList: ticketState["eventGoods"];
  repGoods: ticketState["eventGoodsMap"][number];
  selectablePlaces: ComponentProps<typeof GoodsVenueSelector>["venues"];
  isAdmin: boolean;
  watchlistItemIds: Set<string>;
  handleClickWatch: (
    goodsId: string
  ) => React.MouseEventHandler<HTMLButtonElement>;
};
export const EventGoodsItemInfo: React.FC<EventGoodsItemInfoProps> = (
  props
) => {
  const MAX_GOODS_PURCHASE = 99;
  const isLogined = useSelector((state: Store) => Boolean(state.auth.user.uid));
  const { selectedPlaceCode, havingTicketPlaceCodes } = useSelector(
    (state: Store) => state.event.goodsPageSettings
  );
  const isAddressRegistered = useSelector(
    (state: Store) => state.auth.isAddressRegistered
  );
  const displayEvent = useSelector((state: Store) => state.event.displayEvent);
  const dispatch = useDispatch();
  const maxDisplayCount =
    // @ts-expect-error TS18048
    props.repGoods?.purchaseLimit > MAX_GOODS_PURCHASE
      ? MAX_GOODS_PURCHASE
      : props.repGoods?.purchaseLimit;
  const isOnSale =
    (displayEvent.goodsSaleStatus === "ON_SALE" &&
      isDisplayPeriod(
        props.repGoods.display,
        props.repGoods.releaseDateTime,
        props.repGoods.closeDateTime
      )) ||
    props.isAdmin;
  const classesGoodsInfoDesign = goodsInfoDesign({ isOnSale });
  const { t } = useI18n();
  const price = getDisplayPrice(props.repGoods);

  const incrementNum = (itemId: string) => {
    const element = document.getElementById(itemId) as HTMLInputElement;
    const { value } = element;

    if (Number(value) === maxDisplayCount) {
      element.value = value;
    } else {
      element.value = String(Number(value) + 1);
    }
  };
  const decrementNum = (itemId: string) => {
    const element = document.getElementById(itemId) as HTMLInputElement;
    const { value } = element;

    if (Number(value) === 0) {
      element.value = value;
    } else {
      element.value = String(Number(value) - 1);
    }
  };

  const openLoginModal = () => {
    dispatch(modalActions.toggleLoginMethod());
  };

  /**
   * グッズをカートに追加する
   */
  const addToCart = () => {
    const productList: CartProduct[] = [];
    props.classGoodsList.forEach((item) => {
      const itemId = item._id;
      // @ts-expect-error TS2345
      const element = document.getElementById(itemId) as HTMLInputElement;
      if (element) {
        const selectedCount: string = element.value;
        productList.push({
          // @ts-expect-error TS2322
          id: itemId,
          productType: "goods",
          count: Number(selectedCount),
          price: item.price_jpy,
          name: item.name,
          // @ts-expect-error TS2322
          variantName: item.subClassName,
        });
      }
    });

    const addCartProduct = productList.filter((product) => product.count !== 0);
    // 購入する商品の個数が0の時処理を止める
    if (addCartProduct.length === 0) {
      return;
    }
    dispatch(
      cartActions.addToCart.started({
        body: {
          eventId: props.eventId,
          products: addCartProduct,
        },
        actionOnModal: () =>
          dispatch(cartActions.toggleMyCart({ isOpen: true })),
      })
    );
  };

  const setSelectedPlaceCode = (selectedPlaceCode: string) => {
    dispatch(
      eventActions.setGoodsPageSettings({
        selectedPlaceCode,
      })
    );
  };

  // 商品がすべて売り切れの場合はカートボタンを売り切れ状態にする
  const isSoldOutCartButton = props.classGoodsList.every(
    (item) => item.remaining === -1
  );

  const {
    analyticsProviderItem,
    handleAddToCart,
    handleChangeAmount,
    handleDecreaseAmount,
    handleIncreaseAmount,
    handleSelectVariant,
    maybeId,
    selectedVariantId,
    variantAmount,
    variantCategories,
  } = useEventGoodsItemInfo(props);
  const rootStyles = [
    classesGoodsInfoDesign.root,
    isFeatureEnabled({ featureName: "newGoodsDetail" }) &&
      classesGoodsInfoDesign.featureRoot,
  ];

  return (
    <div css={rootStyles}>
      <FeatureEnabled
        currentComponent={
          <Fragment>
            <h2>{props.repGoods?.name}</h2>
            <p {...withNoTranslate()} css={classesGoodsInfoDesign.price}>
              {
                // if there is valiation
                props.repGoods?.subClassName
                  ? t("goods.each", {
                      price: `¥${price.toLocaleString()}`,
                    })
                  : `¥${price.toLocaleString()}`
              }
            </p>

            {props.repGoods.fulfillmentShipment && (
              <p css={classesGoodsInfoDesign.shipmentTitle}>
                {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                {props.repGoods.fulfillmentShipment.title} 発送予定
              </p>
            )}

            {/* 販売期間がある場合は表示する */}
            {/* 既存のグッズ詳細に記載されている販売期間をこちらに移動させる必要あり */}
            {/* <p css={classesGoodsInfoDesign.salesPeriod}>販売期間：04/24(金)18:00 〜 05/10(日)23:59</p> */}

            {/* サイズ選択がある場合は表示する */}
            <div css={classesGoodsInfoDesign.selectSize}>
              {props.classGoodsList
                ? props.classGoodsList.map((item, index) => {
                    // 売り切れの場合は売り切れラベルを表示する
                    const isSoldOut = item.remaining === -1;
                    return (
                      <div
                        css={classesGoodsInfoDesign.selectSizeForm}
                        key={index}
                      >
                        <p css={classesGoodsInfoDesign.selectSizeLabel}>
                          {item.subClassName}
                        </p>
                        {!isSoldOut ? (
                          <div css={classesGoodsInfoDesign.selectSizeButton}>
                            {/*
                       // @ts-expect-error TS2345 */}
                            <div onClick={() => decrementNum(item._id)}>
                              <RemoveIcon />
                            </div>
                            <input
                              id={item._id}
                              type="text"
                              defaultValue={`0`}
                            />
                            {/*
                       // @ts-expect-error TS2345 */}
                            <div onClick={() => incrementNum(item._id)}>
                              <AddIcon />
                            </div>
                          </div>
                        ) : (
                          EventGoodsStatusBadges.SOLD_OUT
                        )}
                        {item._id && (
                          <AnalyticsProvider
                            item={{
                              eventId: props.eventId,
                              eventGoodsFirestoreId: item._id,
                              itemName: item.name,
                              price: item.price_jpy,
                              itemVariant: item.subClassName,
                            }}
                          >
                            <WatchlistIconButton
                              isWatched={props.watchlistItemIds.has(item._id)}
                              onClick={props.handleClickWatch(item._id)}
                            />
                          </AnalyticsProvider>
                        )}
                      </div>
                    );
                  })
                : null}
            </div>

            {/* 配送・会場を含め選択肢をだす */}
            <div css={classesGoodsInfoDesign.placeSelector}>
              <GoodsVenueSelector
                selectedPlaceCode={selectedPlaceCode}
                venues={props.selectablePlaces}
                setSelectedPlaceCode={setSelectedPlaceCode}
              />
            </div>

            <AddToCartButton
              isOnSale={isOnSale}
              isSoldOut={isSoldOutCartButton}
              isLogined={isLogined}
              // @ts-expect-error TS2322
              isAddressRequired={
                isLogined &&
                isShippingItem(props.repGoods) &&
                !isAddressRegistered
              }
              // 会場チケットを購入していないと会場グッズが購入できない設定をデフォルトでは外す。会場グッズのみ売りたいイベントがあるため。
              // TODO 会場グッズが増えてきたらもう少し仕様を詰めたい
              // @ts-expect-error TS2322
              isNeedVenueTicket={
                displayEvent?.goodsInfo?.isVenueTicketRequired &&
                isLogined &&
                !isShippingItem(props.repGoods) &&
                !havingTicketPlaceCodes.includes(selectedPlaceCode)
              }
              ticketUrl={
                displayEvent.ticketUrl || // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
                `/events/${displayEvent.eventId}/ticket`
              }
              openLoginModal={openLoginModal}
              addToCart={addToCart}
            />

            <ul css={classesGoodsInfoDesign.tags}>
              {props.repGoods?.hashtags
                ? props.repGoods.hashtags.map((tag, index) => {
                    return <li key={index}>{tag}</li>;
                  })
                : null}
            </ul>
          </Fragment>
        }
        featureComponent={
          <Fragment>
            {/* TODO: 出し分けの条件追加 https://github.com/balus-co-ltd/spwn/issues/11447 */}
            <div>
              <VenueLabel />
            </div>

            <GoodsName text={props.repGoods?.name} />

            <Price price={price} />

            <FulfillmentSelect
              onFulfillmentSelect={setSelectedPlaceCode}
              places={props.selectablePlaces}
              selectedPlaceCode={selectedPlaceCode}
            />

            <Variant
              handleSelectVariant={handleSelectVariant}
              selectedVariantId={selectedVariantId}
              variantCategories={variantCategories}
            />

            <Amount
              amount={variantAmount}
              // TODO: 可変値に変える https://github.com/balus-co-ltd/spwn/issues/11446
              isDisabled={false}
              isError={false}
              onAmountChange={handleChangeAmount}
              onAmountDecrement={handleDecreaseAmount}
              onAmountIncrement={handleIncreaseAmount}
            />

            <ActionButtons
              // TODO: isDisabled を可変させる
              primaryButton={
                <AddToCartButtonV2
                  addToCart={handleAddToCart}
                  isDisabled={false}
                />
              }
              secondaryButton={
                <Fragment>
                  {maybeId !== undefined &&
                    analyticsProviderItem !== undefined && (
                      <AnalyticsProvider item={analyticsProviderItem}>
                        <WatchListButton itemId={maybeId} />
                      </AnalyticsProvider>
                    )}
                </Fragment>
              }
            />

            <TagList tagList={props.repGoods?.hashtags} />
          </Fragment>
        }
        featureName="newGoodsDetail"
      />

      {props.repGoods?.specification ? (
        <div
          css={classesGoodsInfoDesign.text}
          dangerouslySetInnerHTML={{
            __html: sanitizer(props.repGoods.specification),
          }}
        />
      ) : null}
    </div>
  );
};

type GoodsInfoDesignProps = {
  isOnSale: boolean;
};
const goodsInfoDesign = (_props?: GoodsInfoDesignProps) => {
  // const buttonState = props.isOnSale ? "#00c2ae" : "#e0e0e0";

  return {
    root: css`
      width: 90%;
      margin: 0 auto;
      @media screen and (min-width: 768px) {
        width: calc(41% - 17px);
      }
      h2 {
        margin-bottom: 20px;
        font-size: 20px;
        line-height: 1.5em;
      }
    `,
    featureRoot: css`
      display: grid;
      row-gap: 24px;
      @media screen and (min-width: 768px) {
        /* デザインに合うように目視で合わせた結果の数字 */
        width: calc(33% - 17px);
      }
    `,
    price: css`
      margin-bottom: 16px;
      font-size: 18px;
      font-weight: bold;
    `,
    shipmentTitle: css`
      color: #69afe5;
      font-size: 14px;
      margin-bottom: 32px;
    `,
    salesPeriod: css`
      padding: 12px 20px;
      margin-bottom: 20px;
      color: #fc1f74;
      font-size: 14px;
      text-align: center;
      border: 1px solid #fc1f74;
    `,

    selectSize: css`
      margin-bottom: 20px;
    `,
    selectSizeForm: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0px 0px;
      border: 2px solid #e5e5e5;
      &:not(:last-child) {
        margin-bottom: 10px;
      }
    `,
    selectSizeLabel: css`
      flex: 1;
      padding-left: 12px;
    `,
    selectSizeButton: css`
      display: flex;
      width: 75px;
      margin-left: 15px;
      border: 1px solid #e5e5e5;
      border-radius: 3px;
      input {
        width: calc(100% / 3);
        padding: 0 4px;
        font-size: 14px;
        text-align: center;
      }
      div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: calc(100% / 3);
        padding: 5px 0;
        border-left: 1px solid #e5e5e5;
        background-color: #f4f4f4;
        cursor: pointer;
        svg {
          width: 15px;
          height: 15px;
        }
      }
    `,
    tags: css`
      display: flex;
      flex-wrap: wrap;
      padding-top: 15px;
      margin-bottom: calc(25px - 8px);
      border-top: 1px solid #e5e5e5;
      li {
        padding: 5px 15px;
        margin-right: 8px;
        margin-bottom: 8px;
        color: #00c2ae;
        font-size: 12px;
        background-color: #eefaf9;
        border-radius: 14px;
      }
    `,
    text: css`
      h3 {
        margin: 15px 0 10px;
      }
      p {
        color: #3c3c3c;
        font-size: 14px;
        line-height: 1.5em;
      }
      ul {
        li {
          color: #3c3c3c;
          font-size: 14px;
          line-height: 1.5em;
        }
      }
    `,
    placeSelector: css`
      display: flex;
      justify-content: flex-end;
      margin-bottom: 10px;
    `,
  };
};
